import React, { useState, useContext, useEffect } from "react";
import { useQuery, useMutation } from "@apollo/react-hooks";
import {
    CardNumberElement,
    useStripe,
    useElements
} from "@stripe/react-stripe-js";
import PropTypes from "prop-types";

// components:
import LoadingSpinner from "core/LoadingSpinner";
import CheckoutLayout from "core/CheckoutLayout";
import CheckoutForm from "core/CheckoutForm2";
import TruncateText from "core/TruncateText";
import Hidden from "core/Hidden";
import { PhotosContext } from "photos/context";

// mutations/queries:
import { GET_SAVED_CARD } from "./queries";
import { ADD_PURCHASED_PHOTO } from "./mutations";
import { PhotoDetailDocument } from "js/graphql";

import {
    NAME_FULL_RES,
    NAME_LARGE_RES,
    getLicenseSchema,
    getPrice,
    handleGaEvents
} from "photos/helpers";

// reusable data / helpers:
import { PHOTO_DETAIL, PHOTO_CHECKOUT } from "data/links";

const PhotoCheckout = ({ photo, selectedLicense }) => {
    const {
        id: photoId,
        slugWithId,
        assets: { thumbnailMedium: thumbnail },
        title,
        creator
    } = photo;

    useEffect(() => {
        handleGaEvents({
            photoId,
            licenseName: selectedLicense,
            checkout: true
        });
    }, [selectedLicense]);

    // set up order-preview & order-total props
    const selectedPrice = getPrice(selectedLicense);
    const setLicense = licenseName => {
        history.replace(
            PHOTO_CHECKOUT.replace(":purchaseType", licenseName).replace(
                ":photoId",
                photoId
            )
        );
    };
    const schemaProps = {
        photo,
        selected: selectedLicense,
        setSelected: setLicense
    };
    const licenseSchemas = [
        getLicenseSchema(NAME_LARGE_RES, schemaProps),
        getLicenseSchema(NAME_FULL_RES, schemaProps)
    ];

    // default empty form:
    const initialForm = {
        name: "",
        address_zip: "",
        receipt_info: ""
    };

    // set up/assign const states:
    const [saveCard, setSaveCard] = useState(false);
    const [form, setForm] = useState(initialForm);
    const [purchased, setPurchased] = useState(false);
    const [formError, setFormError] = useState(null);

    const { history } = useContext(PhotosContext);
    const stripe = useStripe();
    const elements = useElements();

    const photoDetailPagePath = PHOTO_DETAIL.replace(
        ":photoSlugWithId",
        slugWithId
    );
    const isSubmitDisabled = false;

    // submit purchase photo functions:
    const handlePurchaseComplete = ({ purchasePhoto }) => {
        if (purchasePhoto.ok) {
            setPurchased(true);
            setFormError(null);

            // add photo purchase type to redirect URL after checkout - ?purchase parameter is for analytics:
            const photoPurchaseType = `?purchase=${selectedLicense.toLowerCase()}`;
            const photoDetailUrl = encodeURI(
                PHOTO_DETAIL.replace(":photoSlugWithId", slugWithId) +
                    photoPurchaseType
            );

            // Don't show the checkout form again if the user clicks "back"
            window.location.replace(photoDetailUrl);
        } else if (purchasePhoto.errors) {
            setFormError(purchasePhoto.errors);
        }
    };

    const [purchasePhoto] = useMutation(ADD_PURCHASED_PHOTO, {
        onCompleted: handlePurchaseComplete
    });

    const submitFormWithNewCardInfo = async e => {
        e.preventDefault();

        if (!form.name) {
            return setFormError("Please fill out all required fields");
        }

        const cardNumberElement = elements.getElement(CardNumberElement);
        const { token, error } = await stripe.createToken(
            cardNumberElement,
            form
        );

        if (error) {
            setFormError(error.message);
        } else {
            purchasePhoto({
                variables: {
                    input: {
                        purchaseType: selectedLicense.toUpperCase(),
                        photoId,
                        newCard: {
                            token: token.id,
                            name: form.name,
                            country: "U.S.",
                            receiptInfo: form.receipt_info
                                ? form.receipt_info
                                : "",
                            saveCard
                        }
                    }
                }
            });
        }
    };

    const submitFormWithSavedCardInfo = e => {
        e.preventDefault();
        purchasePhoto({
            variables: {
                input: {
                    purchaseType: selectedLicense.toUpperCase(),
                    photoId,
                    savedCard: { cardId: response.id }
                }
            }
        });
    };

    const handleFormChange = (value, fieldName) => {
        setForm({ ...form, [fieldName]: value });
        setFormError(null);
    };

    const saveCardCheckboxOnClick = () => {
        setSaveCard(!saveCard);
    };

    // query for saved card data:
    const { loading, data: { savedCard: response = {} } = {} } = useQuery(
        GET_SAVED_CARD,
        {
            notifyOnNetworkStatusChange: true
        }
    );

    if (loading) {
        return <LoadingSpinner />;
    }

    const hasSavedCard =
        response && Object.entries(response).length >= 1 ? true : false;

    return (
        <CheckoutLayout
            pageHeading="Purchase Photo"
            thumbnail={thumbnail}
            imageTitle={title}
            slugWithId={slugWithId}
            creator={creator}
        >
            <CheckoutForm
                hasSavedCard={hasSavedCard}
                saveCardCheckboxOnClick={saveCardCheckboxOnClick}
                saveCardInitialValue={saveCard}
                response={response}
                form={form}
                handleFormChange={handleFormChange}
                generalFormError={formError}
                selectedPrice={selectedPrice}
                title={title}
                thumbnail={thumbnail}
                slugWithId={slugWithId}
                licenseSchemas={licenseSchemas}
                isSubmitDisabled={isSubmitDisabled}
                submitFormWithNewCardInfo={submitFormWithNewCardInfo}
                submitFormWithSavedCardInfo={submitFormWithSavedCardInfo}
                cancelLink={photoDetailPagePath}
            />
        </CheckoutLayout>
    );
};

PhotoCheckout.propTypes = {
    selectedLicense: PropTypes.string.isRequired,
    photo: PropTypes.object.isRequired
};

export default PhotoCheckout;
